import React from 'react';

/**
 * Button Component
 *
 * Primary UI button component using the Coord design system.
 * Supports multiple variants, sizes, and states.
 *
 * @example
 * <Button variant="primary" size="md">Click me</Button>
 * <Button variant="secondary" icon={<Icon />}>With icon</Button>
 * <Button variant="outline" loading>Loading...</Button>
 */
const Button = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
  icon,
  iconRight,
  fullWidth = false,
  ...props
}) => {
  // Base styles shared by all buttons
  const baseClasses = [
    'inline-flex items-center justify-center font-medium rounded-lg',
    'transition-all duration-200 ease-smooth',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    'active:scale-[0.98]',
    'relative overflow-hidden',
    fullWidth && 'w-full'
  ].filter(Boolean).join(' ');

  // Size variants
  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs gap-1.5',
    sm: 'px-3 py-2 text-sm gap-2',
    md: 'px-4 py-2.5 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5',
    xl: 'px-8 py-4 text-xl gap-3'
  };

  // Variant styles using design system tokens
  const variantClasses = {
    // Primary - main CTA, peach/coral
    primary: [
      'bg-primary text-white shadow-sm',
      'hover:bg-primary-600 hover:shadow-primary',
      'focus-visible:ring-primary',
    ].join(' '),

    // Secondary - sage green
    secondary: [
      'bg-sage text-white shadow-sm',
      'hover:bg-sage-600 hover:shadow-sage',
      'focus-visible:ring-sage',
    ].join(' '),

    // Tertiary - neutral, for less prominent actions
    tertiary: [
      'bg-neutral-100 text-text-primary border border-border',
      'hover:bg-neutral-200 hover:border-border-dark',
      'focus-visible:ring-neutral-400',
    ].join(' '),

    // Outline - bordered button (1px border for lighter feel)
    outline: [
      'bg-transparent text-primary border border-primary',
      'hover:bg-primary-50 hover:border-primary-600',
      'focus-visible:ring-primary',
    ].join(' '),

    // Ghost - minimal, no background
    ghost: [
      'bg-transparent text-text-secondary',
      'hover:bg-neutral-100 hover:text-text-primary',
      'focus-visible:ring-neutral-400',
    ].join(' '),

    // Danger - destructive actions
    danger: [
      'bg-error text-white shadow-sm',
      'hover:bg-error-600 hover:shadow-md',
      'focus-visible:ring-error',
    ].join(' '),

    // Danger outline - less aggressive destructive (1px border)
    'danger-outline': [
      'bg-transparent text-error border border-error',
      'hover:bg-error-50 hover:border-error-600',
      'focus-visible:ring-error',
    ].join(' '),

    // Text - link-like button
    text: [
      'bg-transparent text-primary px-1 py-0.5',
      'hover:text-primary-700 hover:underline underline-offset-2',
      'focus-visible:ring-primary',
    ].join(' '),

    // Success - for confirmations
    success: [
      'bg-success text-white shadow-sm',
      'hover:bg-success-600 hover:shadow-md',
      'focus-visible:ring-success',
    ].join(' '),
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <span className="spinner spinner-sm" aria-hidden="true" />
  );

  // Utility to join class names
  const cn = (...classes) => classes.filter(Boolean).join(' ');

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(baseClasses, sizeClasses[size], variantClasses[variant], className)}
      aria-busy={loading}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {icon && !loading && (
        <span className="flex-shrink-0" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
      {iconRight && !loading && (
        <span className="flex-shrink-0" aria-hidden="true">
          {iconRight}
        </span>
      )}
    </button>
  );
};

export default Button;
